<template>
  <div class="booking-details-page">
    <v-container>
      <!-- Back Button and Header -->
      <div class="d-flex align-center mb-6">
        <v-btn icon @click="$router.go(-1)" class="mr-4">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="page-title">Booking Details</h1>
        </div>
        <v-spacer></v-spacer>
        <div v-if="booking" class="status-badge-wrapper">
          <div class="status-badge" :class="booking.status.toLowerCase()">
            {{ booking.status }}
          </div>
        </div>
      </div>

      <!-- Global Loading State -->
      <global-loader v-if="loading" message="Loading booking details..." />

      <!-- Error State -->
      <v-alert v-else-if="error" type="error" outlined class="mb-6">
        {{ error }}
      </v-alert>

      <!-- Main Content -->
      <v-row v-else-if="booking">
        <v-col cols="12" lg="8">
          <!-- Service Card -->
          <v-card class="detail-card mb-6" outlined>
            <v-card-title class="section-title">Service Information</v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" sm="4" class="text-center">
                  <v-img
                    v-if="cartItem.image"
                    :src="cartItem.image"
                    class="service-detail-image"
                    contain
                  ></v-img>
                  <div v-else class="service-icon-placeholder">
                    <v-icon size="64" color="#f46523">mdi-tools</v-icon>
                  </div>
                </v-col>
                <v-col cols="12" sm="8">
                  <div class="service-header mb-4">
                    <h2 class="service-name-text">{{ cartItem.title }}</h2>
                  </div>
                  <p class="service-description">{{ cartItem.description || 'No specific instructions provided.' }}</p>
                  
                  <div class="booking-meta mt-6">
                    <div class="meta-item">
                      <v-icon small class="mr-2">mdi-calendar-clock</v-icon>
                      <span>Requested: {{ formatDateTime(cartItem.requested_date_time) }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Address Details -->
          <v-card class="detail-card mb-6" outlined>
            <v-card-title class="section-title">Service Address</v-card-title>
            <v-card-text class="pa-6">
              <div v-if="address" class="address-details">
                <div class="d-flex align-center mb-2">
                  <v-icon color="#f46523" class="mr-3">mdi-map-marker</v-icon>
                  <h3 class="address-name">{{ address.title }}</h3>
                </div>
                <div class="address-content ml-9">
                  <p class="mb-1"><strong>{{ address.name }}</strong></p>
                  <p class="mb-1">{{ address.address_line_1 }}</p>
                  <p v-if="address.landmark" class="mb-1">Landmark: {{ address.landmark }}</p>
                  <p class="mb-1">{{ address.city }}</p>
                  <p class="mb-0">Contact: {{ address.contact_number }}</p>
                </div>
              </div>
              <div v-else class="text-center py-4">No address info found.</div>
            </v-card-text>
          </v-card>

          <!-- History Timeline -->
          <v-card class="detail-card mb-6" outlined>
            <v-card-title class="section-title">Tracking History</v-card-title>
            <v-card-text class="pa-6">
              <v-timeline dense align-top>
                <v-timeline-item
                  v-for="(event, index) in booking.history"
                  :key="index"
                  color="#f46523"
                  small
                >
                  <div class="timeline-content">
                    <div class="d-flex justify-space-between align-center mb-1">
                      <div class="event-status text-capitalize">{{ event.status }}</div>
                      <div class="event-time">{{ formatTime(event.time) }}</div>
                    </div>
                    <div class="event-by text-capitalize">By: {{ event.by }}</div>
                    <div v-if="event.notes" class="event-notes mt-1">{{ event.notes }}</div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="4">
          <!-- Payment Summary -->
          <v-card class="detail-card mb-6" outlined>
            <v-card-title class="section-title">Payment Summary</v-card-title>
            <v-card-text class="pa-6">
              <!-- Transaction Badge -->
              <div v-if="booking.transaction_info" class="transaction-summary mb-6">
                <div class="summary-row">
                  <span class="label">Method:</span>
                  <span class="value text-capitalize">{{ booking.transaction_info.payment_method }}</span>
                </div>
                <div class="summary-row mt-2">
                  <span class="label">Status:</span>
                  <span class="value">
                    <v-chip
                      small
                      :color="booking.transaction_info.payment_status === 'pending' ? 'orange' : 'success'"
                      dark
                      class="text-capitalize"
                    >
                      {{ booking.transaction_info.payment_status }}
                    </v-chip>
                  </span>
                </div>
              </div>

              <v-divider class="mb-4"></v-divider>

              <div class="price-breakdown">
                <div v-for="item in booking.payment_info" :key="item._id" class="price-row mb-3">
                  <span class="price-label text-capitalize">{{ formatPriceLabel(item.title) }}</span>
                  <span class="price-value" :class="{ 'grand-total': item.title === 'grand_total' }">
                    {{ formatCurrency(item.value) }}
                  </span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Assigned Partner -->
          <v-card v-if="booking.assigned_partner" class="detail-card mb-6" outlined>
            <v-card-title class="section-title">Assigned Professional</v-card-title>
            <v-card-text class="pa-6">
              <div class="partner-info d-flex align-center">
                <v-avatar size="64" color="#f5f5f5" class="mr-4">
                  <v-img
                    v-if="booking.assigned_partner.profile_picture"
                    :src="booking.assigned_partner.profile_picture"
                    alt="Professional"
                  />
                  <v-icon v-else size="32" color="#ccc">mdi-account</v-icon>
                </v-avatar>
                <div>
                  <h3 class="partner-name-text">{{ booking.assigned_partner.name }}</h3>
                  <div class="partner-rating d-flex align-center mt-1">
                    <v-rating
                      v-model="partnerRating"
                      background-color="grey lighten-2"
                      color="amber"
                      dense
                      half-increments
                      readonly
                      size="16"
                    ></v-rating>
                    <span class="text-caption grey--text ml-2">(4.8)</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
          <v-card v-else class="detail-card mb-6" outlined>
            <v-card-title class="section-title">Assigned Professional</v-card-title>
            <v-card-text class="pa-8 text-center grey--text">
              <v-icon size="48" color="#eee" class="mb-2">mdi-account-clock-outline</v-icon>
              <div>Waiting to assign a professional...</div>
            </v-card-text>
          </v-card>

        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { getBookingById } from "../services/booking.service";
import GlobalLoader from "../components/GlobalLoader.vue";

export default {
  name: "BookingDetailsView",
  components: {
    GlobalLoader,
  },
  props: {
    bookingId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      booking: null,
      loading: false,
      error: null,
      partnerRating: 4.8,
    };
  },
  computed: {
    cartItem() {
      return this.booking?.cart_items?.[0] || {};
    },
    address() {
      return this.cartItem?.details?.address || null;
    },
  },
  mounted() {
    this.fetchBookingDetails();
  },
  methods: {
    async fetchBookingDetails() {
      this.loading = true;
      this.error = null;
      try {
        const response = await getBookingById(this.bookingId);
        // Safely check for error_code 0
        if (response.data && response.data.error_code === 0) {
          const apiData = response.data.data;
          // Check if data itself is the booking or if it contains a bookings array
          if (apiData) {
            if (apiData.bookings && Array.isArray(apiData.bookings)) {
              this.booking = apiData.bookings[0];
            } else {
              this.booking = apiData;
            }
          }
        } else {
          this.error = response.data?.message || "Failed to load booking details.";
        }
      } catch (error) {
        console.error("Error fetching booking details:", error);
        this.error = "Something went wrong. Please try again later.";
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "";
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    formatTime(dateTimeString) {
      if (!dateTimeString) return "";
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    formatPriceLabel(label) {
      return label.replace(/_/g, " ");
    },
    formatCurrency(value) {
      if (value === "N/A" || !value) return "N/A";
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(num);
    },
  },
};
</script>

<style scoped>
.booking-details-page {
  padding-top: 40px;
  padding-bottom: 80px;
  background-color: #fcfcfc;
  min-height: calc(100vh - 80px);
}

.page-title {
  font-size: 34px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.page-subtitle {
  color: #999;
  font-size: 14px;
  font-family: monospace;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.booked,
.status-badge.pending { background: #fff8e1; color: #ffa000; }
.status-badge.confirmed { background: #e8f5e9; color: #43a047; }
.status-badge.completed { background: #e3f2fd; color: #1e88e5; }
.status-badge.cancelled { background: #ffebee; color: #e53935; }

.detail-card {
  border-radius: 16px !important;
  border: 1px solid #eee !important;
  background: white !important;
  overflow: hidden;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  border-bottom: 1px solid #f5f5f5;
  padding: 16px 24px !important;
}

.service-detail-image {
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  max-width: 200px;
  margin: 0 auto;
}

.service-icon-placeholder {
  width: 100px;
  height: 100px;
  background: #fff3ed;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.service-name-text {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  line-height: 1.2;
}

.service-type-id {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.service-description {
  font-size: 18px;
  color: #555;
  line-height: 1.6;
}

.booking-meta {
  background: #fdfdfd;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #f5f5f5;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #444;
}

.address-name {
  font-size: 22px;
  font-weight: 700;
  color: #333;
}

.address-content p {
  color: #666;
  font-size: 17px;
}

.timeline-content {
  background: #f9f9f9;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
}

.event-status {
  font-weight: 700;
  color: #f46523;
  font-size: 16px;
}

.event-time {
  font-size: 14px;
  color: #999;
}

.event-by {
  font-size: 14px;
  color: #666;
}

.event-notes {
  font-size: 15px;
  color: #555;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-row .label {
  color: #666;
}

.summary-row .value {
  font-weight: 700;
  color: #333;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-label {
  color: #555;
  font-size: 16px;
}

.price-value {
  font-weight: 600;
  color: #1a1a1a;
}

.price-value.grand-total {
  font-size: 24px;
  font-weight: 800;
  color: #f46523;
}

.partner-name-text {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
}

@media (max-width: 600px) {
  .page-title {
    font-size: 30px;
  }
  .section-title {
    font-size: 20px;
  }
  .service-name-text {
    font-size: 22px;
  }
  .address-name {
    font-size: 20px;
  }
  .address-content p {
    font-size: 16px;
  }
}
</style>
